/**
 * Prompt x model outcome grid for the client (2026-09-08).
 *
 * The Prompt-Level Results table is the longest in the report — one row per
 * prompt per model, over a hundred rows. Lisa, 2026-09-01: "this is a long
 * table, but I feel like there may be a better way to represent that."
 *
 * This is that: every scored answer as one cell, prompts down, models across.
 * The shape of the client's presence becomes visible immediately — which
 * prompts reach them at all, which models are blank, whether the hits cluster
 * in one stage. The table stays underneath as the source of truth.
 *
 * EXPLAINED, after Lisa's second look (2026-09-08): "it's only showing 26 —
 * why? ... I also don't like how it's cut off the prompts ... can it explain
 * what it's trying to show?" So the grid now (1) opens with its arithmetic —
 * 26 prompts × 4 models = 104 answers, one cell each; (2) groups the rows
 * under the buyer-journey stage they belong to, with that stage's hit count
 * in the header; (3) shows every prompt in full; and (4) closes with a
 * per-model total, so "which model surfaces us at all" is read off the last
 * row rather than counted by eye.
 *
 * FORM: heatmap on an ORDINAL ramp. Mention -> Citation -> Recommendation are
 * degrees of the same thing, so they take one hue stepped by lightness, not
 * three categorical hues. Validated with the ordinal gate against this app's
 * surface — see `palette.ts`, which also records why `no_mention` is
 * deliberately NOT a ramp step (absence is not a strength, and on this run it
 * is 90% of cells, so a coloured step would have painted the grid a colour that
 * says "something happened").
 */
import { Fragment } from 'react';
import type {
  BenchmarkClassification,
  BenchmarkPrompt,
  BenchmarkResult,
  BuyerJourneyStage,
} from '@aurora/shared';
import { BUYER_STAGE_ORDER, buyerStageLabel } from '@aurora/shared';
import { OUTCOME_NOT_SCORED, OUTCOME_RAMP } from './palette';

/**
 * Labels for the STORED per-answer classification, which is strongest-class-wins
 * and therefore MUTUALLY EXCLUSIVE — unlike the Mention / Citation /
 * Recommendation columns elsewhere in the report, which are three independent,
 * overlapping signals re-derived at aggregation (2026-07-13 D1).
 *
 * They must not share wording, or the grid contradicts the tables above it. The
 * first version of this legend read "Mention (0)" on a run whose signal summary
 * says 9 mentions — both correct, describing different things. These labels say
 * which one this is.
 */
const CLASS_LABEL: Record<BenchmarkClassification, string> = {
  no_mention: 'Not named',
  mention: 'Named only',
  citation: 'Cited, not recommended',
  recommendation: 'Recommended',
  not_scored: 'Not scored',
};

/** Strength order, so repeats of the same prompt collapse to the best outcome. */
const RANK: Record<BenchmarkClassification, number> = {
  not_scored: -1,
  no_mention: 0,
  mention: 1,
  citation: 2,
  recommendation: 3,
};

const named = (c: BenchmarkClassification | undefined): boolean =>
  c === 'mention' || c === 'citation' || c === 'recommendation';

function fillFor(c: BenchmarkClassification): string {
  if (c === 'recommendation') return OUTCOME_RAMP.recommendation;
  if (c === 'citation') return OUTCOME_RAMP.citation;
  if (c === 'mention') return OUTCOME_RAMP.mention;
  if (c === 'not_scored') return OUTCOME_NOT_SCORED;
  return 'transparent';
}

export function PromptOutcomeHeatmap({
  prompts,
  results,
  models,
  targetName,
  visibleCount,
}: {
  prompts: BenchmarkPrompt[];
  results: BenchmarkResult[];
  models: string[];
  targetName: string;
  /**
   * The report's Visibility count for the target (the union of the three
   * signals). It can exceed the number of filled cells here, because an answer
   * that only LINKED to the client's site is visible without ever naming them
   * — and the two numbers sit inches apart on the page, so the lede says why.
   */
  visibleCount?: number;
}): JSX.Element | null {
  if (prompts.length === 0 || models.length === 0 || results.length === 0)
    return null;

  // Collapse repeats (runsPerPrompt > 1) to the strongest outcome per cell, so
  // one prompt x model is always exactly one cell.
  const best = new Map<string, BenchmarkClassification>();
  for (const r of results) {
    const k = `${r.promptId}|${r.model}`;
    const prev = best.get(k);
    if (prev === undefined || RANK[r.targetClassification] > RANK[prev]) {
      best.set(k, r.targetClassification);
    }
  }

  const ordered = [...prompts].sort((a, b) => a.ordinal - b.ordinal);
  const counts = { mention: 0, citation: 0, recommendation: 0, none: 0 };
  const perModel = new Map<string, number>(models.map((m) => [m, 0]));
  for (const p of ordered) {
    for (const m of models) {
      const c = best.get(`${p.id}|${m}`);
      if (c === 'recommendation') counts.recommendation += 1;
      else if (c === 'citation') counts.citation += 1;
      else if (c === 'mention') counts.mention += 1;
      else counts.none += 1;
      if (named(c)) perModel.set(m, (perModel.get(m) ?? 0) + 1);
    }
  }

  // Group by buyer-journey stage, in journey order; prompts keep their
  // run-wide number so the table beneath and the grid agree.
  const stageIdx = (s: BuyerJourneyStage): number => {
    const i = BUYER_STAGE_ORDER.indexOf(s);
    return i < 0 ? 99 : i;
  };
  const stages = [...new Set(ordered.map((p) => p.stage))].sort(
    (a, b) => stageIdx(a) - stageIdx(b),
  );
  const numberOf = new Map(ordered.map((p, i) => [p.id, i + 1]));
  const groups = stages.map((stage) => {
    const rows = ordered.filter((p) => p.stage === stage);
    let hits = 0;
    for (const p of rows)
      for (const m of models) if (named(best.get(`${p.id}|${m}`))) hits += 1;
    return { stage, rows, hits, cells: rows.length * models.length };
  });

  const totalCells = ordered.length * models.length;
  const totalHits = counts.mention + counts.citation + counts.recommendation;

  return (
    <figure className="prompt-heatmap-wrap">
      <p className="funnel-lede">
        <strong>
          {ordered.length} prompts × {models.length} AI models = {totalCells}{' '}
          answers
        </strong>
        , one cell each — this grid is every answer in the benchmark. A filled
        cell is an answer that named <strong>{targetName}</strong>; its shade is
        the strongest thing that answer did. {targetName} was named in{' '}
        {totalHits} of the {totalCells}
        {visibleCount !== undefined && visibleCount > totalHits ? (
          <>
            {' '}
            — the report counts {visibleCount} as visible because{' '}
            {visibleCount - totalHits}{' '}
            {visibleCount - totalHits === 1 ? 'answer' : 'answers'} linked to
            their website without naming them.
          </>
        ) : (
          '.'
        )}
      </p>
      <div className="prompt-heatmap-scroll">
        <table className="prompt-heatmap">
          <thead>
            <tr>
              <th className="ph-corner">Prompt</th>
              {models.map((m) => (
                <th key={m} className="ph-model" title={m}>
                  {m.split('/').pop()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.stage}>
                <tr className="ph-stage">
                  <th colSpan={models.length + 1}>
                    {buyerStageLabel(g.stage)}
                    <span className="ph-stage-meta">
                      {g.rows.length} {g.rows.length === 1 ? 'prompt' : 'prompts'}{' '}
                      · named in {g.hits} of {g.cells} answers
                    </span>
                  </th>
                </tr>
                {g.rows.map((p) => (
                  <tr key={p.id}>
                    <th className="ph-prompt">
                      <span className="ph-index">{numberOf.get(p.id)}</span>
                      {p.prompt}
                    </th>
                    {models.map((m) => {
                      const c = best.get(`${p.id}|${m}`) ?? 'no_mention';
                      return (
                        <td key={m} className="ph-cell">
                          <span
                            className={
                              c === 'no_mention' ? 'ph-dot is-empty' : 'ph-dot'
                            }
                            style={{ background: fillFor(c) }}
                            title={`${targetName} — ${CLASS_LABEL[c]}\n${m}\n${p.prompt}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="ph-foot">
              <th>Answers naming {targetName}, per model</th>
              {models.map((m) => {
                const n = perModel.get(m) ?? 0;
                return (
                  <td key={m} className={n === 0 ? 'is-zero' : undefined}>
                    {n} of {ordered.length}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="chart-legend">
        <span className="chart-legend-item">
          <span
            className="chart-legend-swatch"
            style={{ background: OUTCOME_RAMP.mention }}
          />
          Named only ({counts.mention})
        </span>
        <span className="chart-legend-item">
          <span
            className="chart-legend-swatch"
            style={{ background: OUTCOME_RAMP.citation }}
          />
          Cited ({counts.citation})
        </span>
        <span className="chart-legend-item">
          <span
            className="chart-legend-swatch"
            style={{ background: OUTCOME_RAMP.recommendation }}
          />
          Recommended ({counts.recommendation})
        </span>
        <span className="chart-legend-item">
          <span className="chart-legend-swatch ph-swatch-empty" />
          Not named ({counts.none})
        </span>
      </div>
      <figcaption className="muted ranked-bars-caption">
        One cell per prompt per model — the single strongest outcome{' '}
        <strong>{targetName}</strong> achieved in that answer. Empty cells are
        answers that did not name them at all. These four buckets are mutually
        exclusive, so their totals differ from the Mention, Citation and
        Recommendation counts elsewhere in this report — those are three
        independent signals that overlap, and one answer can count in all three.
      </figcaption>
    </figure>
  );
}
